<template>
<div>
	<v-card elevation="1" class="mb-offset background-2 round-card mt-4" width="100%">
		<!-- Header -->
		<v-card-title class="px-3 py-2">
			<v-badge v-model="hasDuplicates" color="warning">
				<v-icon slot="badge" dark>mdi-alert</v-icon>
				<div class="d-flex">
					<p class="pl-2 pl-xl-3">{{ $trans(config.transPlural) }} Duplicati</p>
					<div class="fs12 grey--text ml-2 ma-auto mb-0">
						({{ duplicateGroups.length }} gruppi di duplicati trovati)
					</div>
				</div>
			</v-badge>
			<v-spacer></v-spacer>

			<!-- Cross-model toggle -->
			<v-switch
				v-model="crossModel"
				label="Cerca tra tutti i modelli"
				color="primary"
				hide-details
				inset
				class="mr-3 mt-0 pt-0"
				@change="onCrossModelToggle"
			></v-switch>

			<!-- Included types (visible only when cross-model is on) -->
			<v-select
				v-if="crossModel"
				v-model="includedTypes"
				:items="availableTypes"
				item-text="text"
				item-value="value"
				label="Tipi da includere"
				multiple
				chips
				small-chips
				outlined
				dense
				hide-details
				style="max-width: 260px;"
				class="mr-2"
				@change="fetchDuplicates"
			></v-select>

			<!-- Search -->
			<v-text-field
				class="hidden-xs-only font-weight-regular"
				append-icon="search"
				label="Cerca"
				v-model="search"
				clearable
				hide-details
				outlined
				dense
			></v-text-field>

			<!-- Refresh button -->
			<v-btn icon @click="fetchDuplicates" class="ml-2">
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-icon v-on="on">mdi-refresh</v-icon>
					</template>
					<span>Aggiorna</span>
				</v-tooltip>
			</v-btn>
		</v-card-title>
		<v-divider></v-divider>

		<!-- Duplicate Detection Criteria -->
		<v-card-text v-if="duplicateFields.length > 0" class="pb-0">
			<v-alert type="info" text dense class="mb-3">
				<div class="d-flex align-center">
					<v-icon left small>mdi-info</v-icon>
					<span class="body-2">
						<strong>Criteri di rilevamento duplicati:</strong>
						I record vengono considerati duplicati se corrispondono nei seguenti campi:
					</span>
				</div>
				<v-chip-group class="mt-2">
					<v-chip
						v-for="field in duplicateFields"
						:key="field"
						small
						color="primary"
						text-color="white">
						{{ getFieldLabel(field) }}
					</v-chip>
				</v-chip-group>
			</v-alert>
		</v-card-text>

		<!-- Loading -->
		<v-card-text v-if="loading" class="text-center py-5">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
			<div class="mt-3">Ricerca duplicati in corso...</div>
		</v-card-text>

		<!-- No Duplicates -->
		<v-card-text v-else-if="!loading && duplicateGroups.length === 0" class="text-center py-5">
			<v-icon size="64" color="grey">mdi-check-circle-outline</v-icon>
			<div class="mt-3 title grey--text">Nessun duplicato trovato</div>
			<div class="mt-1 body-2 grey--text">
				Tutti i {{ $trans(config.transPlural) }} sono unici secondo i criteri configurati
			</div>
		</v-card-text>

		<!-- Duplicate Groups -->
		<v-card-text v-else class="pa-3">
			<v-expansion-panels v-model="expandedPanels" multiple>
				<v-expansion-panel
					v-for="(group) in filteredDuplicateGroups"
					:key="`group-${getPrimary(group).id}`"
					class="mb-3">
					<v-expansion-panel-header>
						<div class="d-flex align-center justify-space-between flex-grow-1">
							<div class="d-flex align-center">
								<v-icon left color="warning">mdi-alert</v-icon>
								<span class="subtitle-1 font-weight-medium">
									{{ getDisplayName(getPrimary(group)) }}
								</span>
								<v-chip
									v-if="crossModel"
									x-small
									class="ml-2"
									:color="getBusinessTypeColor(getPrimary(group).business_type)"
									text-color="white">
									{{ getBusinessTypeLabel(getPrimary(group).business_type) }}
								</v-chip>
								<v-chip small class="ml-3" color="error" text-color="white">
									{{ group.duplicates.length + 1 }} duplicati
								</v-chip>
								<!-- Merge button -->
								<v-btn
									v-if="selectedPrimary[`group-${getPrimary(group).id}`]"
									class="ml-3"
									small
									color="success"
									@click.stop="openMergeDialog(group, `group-${getPrimary(group).id}`)">
									<v-icon left small>mdi-call-merge</v-icon>
									Unisci duplicati
								</v-btn>
							</div>
							<div class="text-right mr-3">
								<span class="caption grey--text">
									Creato: {{ getPrimary(group).created_at | date }}
								</span>
							</div>
						</div>
					</v-expansion-panel-header>

					<v-expansion-panel-content>
						<v-simple-table dense>
							<template v-slot:default>
								<thead>
									<tr>
										<th class="text-center" style="width: 60px;">Principale</th>
										<th
											v-for="field in fieldDefs"
											:key="field.key"
											:style="field.width ? `width: ${field.width}px` : ''"
											class="text-left">
											{{ field.text }}
										</th>
									</tr>
								</thead>
								<tbody>
									<!-- Primary record -->
									<tr class="primary lighten-5">
										<td class="text-center">
											<v-checkbox
												v-model="selectedPrimary[`group-${getPrimary(group).id}`]"
												:value="getPrimary(group).id"
												hide-details
												class="mt-0"
											></v-checkbox>
										</td>
										<td
											v-for="field in fieldDefs"
											:key="field.key"
											:class="{ 'duplicate-field': isFieldDuplicate(group, field.key) }">
											<template v-if="field.key === 'actions'">
												<div class="d-inline-flex align-center">
													<v-chip
														v-if="crossModel"
														x-small
														class="mr-2"
														:color="getBusinessTypeColor(getPrimary(group).business_type)"
														text-color="white">
														{{ getBusinessTypeLabel(getPrimary(group).business_type) }}
													</v-chip>
													<v-btn icon @click="editItem(getPrimary(group))">
														<v-icon color="teal">mdi-pencil</v-icon>
													</v-btn>
													<v-btn icon @click="viewItem(getPrimary(group))">
														<v-icon color="primary">mdi-eye</v-icon>
													</v-btn>
												</div>
											</template>
											<template v-else>
												{{ renderFieldValue(getPrimary(group), field) }}
											</template>
										</td>
									</tr>

									<!-- Duplicates -->
									<tr
										v-for="duplicate in group.duplicates"
										:key="duplicate.id"
										class="duplicate-row">
										<td class="text-center">
											<v-checkbox
												v-model="selectedPrimary[`group-${getPrimary(group).id}`]"
												:value="duplicate.id"
												dense
												hide-details
												class="mt-0"
											></v-checkbox>
										</td>
										<td
											v-for="field in fieldDefs"
											:key="field.key"
											:class="{ 'duplicate-field': isFieldDuplicateValue(getPrimary(group), duplicate, field.key) }">
											<template v-if="field.key === 'actions'">
												<div class="d-inline-flex align-center">
													<v-chip
														v-if="crossModel"
														x-small
														class="mr-2"
														:color="getBusinessTypeColor(duplicate.business_type)"
														text-color="white">
														{{ getBusinessTypeLabel(duplicate.business_type) }}
													</v-chip>
													<v-btn icon @click="editItem(duplicate)">
														<v-icon color="teal">mdi-pencil</v-icon>
													</v-btn>
													<v-btn icon @click="viewItem(duplicate)">
														<v-icon color="primary">mdi-eye</v-icon>
													</v-btn>
												</div>
											</template>
											<template v-else>
												{{ renderFieldValue(duplicate, field) }}
											</template>
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</v-card-text>
	</v-card>

	<!-- Merge Confirmation Dialog -->
	<v-dialog v-model="mergeDialog" max-width="800px">
		<v-card class="pa-0 background-2 round-card">
			<v-card-title class="pa-3">
				<p class="pl-3">Conferma unione duplicati</p>
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text v-if="mergeGroup && selectedPrimary[mergeGroupKey]" class="pt-4">
				<v-alert type="warning" text class="mb-4">
					<strong>Attenzione:</strong> Questa operazione non può essere annullata.
					Tutte le relazioni dei record duplicati verranno trasferite al record principale.
				</v-alert>
				<v-alert v-if="hasCrossTypeMerge" type="error" text class="mb-4" dense>
					<strong>Merge cross-type rilevato.</strong>
					Stai unendo record di tipi diversi. Verifica che tutte le relazioni esclusive del
					tipo sorgente siano trasferibili al tipo di destinazione.
				</v-alert>

				<!-- Primary Info -->
				<div class="mb-4">
					<div class="subtitle-1 font-weight-medium mb-2">Record principale (verrà mantenuto):</div>
					<v-chip color="primary" text-color="white" class="mr-2">
						<v-icon left small>mdi-account-star</v-icon>
						{{ getPrimaryName() }} - {{ getPrimaryDate() | date }}
					</v-chip>
				</div>

				<!-- To Delete -->
				<div class="mb-4">
					<div class="subtitle-1 font-weight-medium mb-2">Record che verranno eliminati:</div>
					<v-chip-group column>
						<v-chip
							v-for="item in getItemsToDelete()"
							:key="item.id"
							color="error"
							text-color="white"
							small>
							<v-icon left x-small>mdi-delete</v-icon>
							{{ item.business_name || item.code }} - {{ item.created_at | date }}
						</v-chip>
					</v-chip-group>
				</div>

				<!-- Relations Preview -->
				<div v-if="mergePreview" class="mb-4">
					<div class="subtitle-1 font-weight-medium mb-2">Relazioni che verranno trasferite:</div>
					<v-simple-table dense>
						<template v-slot:default>
							<tbody>
								<tr v-for="(count, relation) in mergePreview.total_relations" :key="relation">
									<td>{{ getRelationLabel(relation) }}</td>
									<td class="text-right font-weight-medium">{{ count }}</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</div>

				<div v-else-if="mergingInProgress" class="text-center py-3">
					<v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
					<div class="mt-2 body-2">Caricamento anteprima...</div>
				</div>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="closeMergeDialog">Annulla</v-btn>
				<v-btn
					color="error"
					@click="performMerge"
					:loading="mergingInProgress"
					:disabled="!mergePreview">
					<v-icon left>mdi-call-merge</v-icon>
					Conferma Unione
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</div>
</template>

<script>
// Configurazione per ogni modelType supportato. Centralizza tutte le differenze
// tra Lead/Prospect (endpoint, chiavi payload, tipo business, etc.)
const MODEL_CONFIGS = {
	lead: {
		primaryKey: 'lead',
		apiPrefix: 'leads',
		primaryIdField: 'primary_lead_id',
		duplicateIdsField: 'duplicate_lead_ids',
		transSingular: 'lead',
		transPlural: 'leads',
		businessTypeCode: 2,
		crossModelDefaults: [3, 0]
	},
	prospect: {
		primaryKey: 'prospect',
		apiPrefix: 'prospects',
		primaryIdField: 'primary_prospect_id',
		duplicateIdsField: 'duplicate_prospect_ids',
		transSingular: 'prospect',
		transPlural: 'prospects',
		businessTypeCode: 3,
		crossModelDefaults: [2, 0]
	}
}

export default {
	name: 'business-duplicates',
	props: {
		modelType: {
			type: String,
			required: true,
			validator: v => Object.keys(MODEL_CONFIGS).includes(v)
		}
	},
	data() {
		return {
			loading: false,
			search: '',
			duplicateGroups: [],
			duplicateFields: [],
			fieldDefs: [],
			expandedPanels: [],
			fieldLabels: {},
			selectedPrimary: {},
			mergeDialog: false,
			mergeGroup: null,
			mergeGroupKey: null,
			mergePreview: null,
			mergingInProgress: false,
			crossModel: false,
			includedTypes: []
		}
	},

	computed: {
		config() {
			return MODEL_CONFIGS[this.modelType]
		},

		availableTypes() {
			// Tutti i tipi Business escluso quello corrente. Etichette tradotte per-tenant.
			const all = [
				{ value: 2, key: 'leads' },
				{ value: 3, key: 'prospects' },
				{ value: 0, key: 'companies' }
			]
			return all
				.filter(t => t.value !== this.config.businessTypeCode)
				.map(t => ({ value: t.value, text: this.$trans(t.key) }))
		},

		hasDuplicates() {
			return this.duplicateGroups.length > 0
		},

		hasCrossTypeMerge() {
			if (!this.mergeGroup || !this.selectedPrimary[this.mergeGroupKey]) return false
			const all = [this.getPrimary(this.mergeGroup), ...this.mergeGroup.duplicates]
			const types = new Set(all.map(l => l.business_type))
			return types.size > 1
		},

		filteredDuplicateGroups() {
			if (!this.search) return this.duplicateGroups

			const searchLower = this.search.toLowerCase()
			return this.duplicateGroups.filter(group => {
				if (this.searchInItem(this.getPrimary(group), searchLower)) return true
				return group.duplicates.some(dup => this.searchInItem(dup, searchLower))
			})
		}
	},

	mounted() {
		this.loading = true
		this.fetchDuplicateSettings()
			.then(() => {
				this.$store.commit('endProgress')
				return this.fetchDuplicates()
			})
			.finally(() => {
				this.loading = false
			})
	},

	methods: {
		getPrimary(group) {
			// Il backend usa il nome singolare del modello come chiave (lead/prospect)
			return group[this.config.primaryKey] || group.primary || {}
		},

		async fetchDuplicates() {
			this.loading = true
			try {
				const payload = {
					cross_model: this.crossModel ? 1 : 0,
					included_types: this.crossModel ? this.includedTypes : []
				}
				const response = await this.axios.post(`/${this.config.apiPrefix}-duplicates`, payload)
				this.duplicateGroups = response.data.data || []
				this.expandedPanels = this.duplicateGroups.slice(0, 3).map((_, i) => i)
				this.selectedPrimary = {}
			} catch (error) {
				console.error('Error fetching duplicates:', error)
			} finally {
				this.loading = false
			}
		},

		async fetchDuplicateSettings() {
			try {
				const response = await this.axios.get(`/${this.config.apiPrefix}-duplicates-info`)
				this.duplicateFields = response.data.duplicate_fields || []
				this.fieldLabels = response.data.field_labels || {}
				this.fieldDefs = response.data.fieldDefs || []
				this.crossModel = !!response.data.cross_model_enabled
				if (Array.isArray(response.data.cross_model_types) && response.data.cross_model_types.length > 0) {
					this.includedTypes = response.data.cross_model_types
				} else {
					this.includedTypes = this.config.crossModelDefaults.slice()
				}
			} catch (error) {
				console.error('Error fetching duplicate settings:', error)
			}
		},

		onCrossModelToggle(enabled) {
			// Attivando il flag, preseleziona tutti i tipi disponibili
			if (enabled) {
				this.includedTypes = this.availableTypes.map(t => t.value)
			}
			this.fetchDuplicates()
		},

		getBusinessTypeLabel(type) {
			const keys = { 0: 'company', 2: 'lead', 3: 'prospect' }
			const key = keys[type]
			return key ? this.$trans(key) : ''
		},

		getBusinessTypeColor(type) {
			const colors = {
				0: 'blue',
				1: 'brown',
				2: 'green',
				3: 'orange'
			}
			return colors[type] !== undefined ? colors[type] : 'grey'
		},

		getRouteBaseForType(type) {
			const routes = { 0: 'companies', 1: 'suppliers', 2: 'leads', 3: 'prospects' }
			return routes[type] || this.config.apiPrefix
		},

		viewItem(item) {
			const base = this.getRouteBaseForType(
				item.business_type !== undefined ? item.business_type : this.config.businessTypeCode
			)
			window.open(`#/${base}/${item.id}`, '_blank')
		},

		editItem(item) {
			const base = this.getRouteBaseForType(
				item.business_type !== undefined ? item.business_type : this.config.businessTypeCode
			)
			window.open(`#/${base}/${item.id}/edit`, '_blank')
		},

		searchInItem(item, searchTerm) {
			return Object.keys(item).some(key => {
				const value = item[key]
				return value && typeof value === 'string' && value.toLowerCase().includes(searchTerm)
			})
		},

		isFieldDuplicate(group, fieldKey) {
			if (!this.duplicateFields.includes(fieldKey)) return false
			const primary = this.getPrimary(group)
			const value = primary[fieldKey]
			if (!value) return false
			return group.duplicates.some(dup => dup[fieldKey] === value)
		},

		isFieldDuplicateValue(primary, duplicate, fieldKey) {
			if (!this.duplicateFields.includes(fieldKey)) return false
			return primary[fieldKey] && duplicate[fieldKey] && primary[fieldKey] === duplicate[fieldKey]
		},

		getFieldLabel(field) {
			return this.fieldLabels[field] || field
		},

		getDisplayName(item) {
			if (item.business_name) return item.business_name
			if (item.name && item.surname) return `${item.name} ${item.surname}`
			if (item.name) return item.name
			if (item.code) return item.code
			return `#${item.id}`
		},

		renderFieldValue(item, field) {
			const value = item[field.value || field.key]

			if (field.key === 'actions') return ''

			if (field.type === 'chip' || field.subtype === 'chip' || field.type === 'general_type') {
				const textValue = item[field.value + '_text'] || item[field.text_value] || value
				return textValue || '-'
			}

			if (field.type === 'date' || field.type === 'datetime') {
				if (!value) return '-'
				return this.$options.filters.date(value)
			}

			if (field.type === 'boolean') {
				return value ? 'Si' : 'No'
			}

			if (field.type === 'currency') {
				if (!value) return '-'
				return this.$options.filters.currency(value)
			}

			return value || '-'
		},

		openMergeDialog(group, groupKey) {
			this.mergeGroup = group
			this.mergeGroupKey = groupKey
			this.mergeDialog = true
			this.mergePreview = null
			this.loadMergePreview()
		},

		async loadMergePreview() {
			this.mergingInProgress = true

			try {
				const itemsToDelete = this.getItemsToDelete()
				const duplicateIds = itemsToDelete.map(item => item.id)

				const response = await this.axios.post(`/${this.config.apiPrefix}-merge-preview`, {
					[this.config.duplicateIdsField]: duplicateIds
				})

				this.mergePreview = response.data.data
			} catch (error) {
				console.error('Error loading merge preview:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.mergingInProgress = false
			}
		},

		async performMerge() {
			this.mergingInProgress = true

			try {
				const primaryId = this.selectedPrimary[this.mergeGroupKey]
				const itemsToDelete = this.getItemsToDelete()
				const duplicateIds = itemsToDelete.map(item => item.id)

				const response = await this.axios.post(`/${this.config.apiPrefix}-merge`, {
					[this.config.primaryIdField]: primaryId,
					[this.config.duplicateIdsField]: duplicateIds
				})

				this.$store.commit('setSnackbar', {
					text: response.data.message || 'Record uniti con successo',
					value: true,
					color: 'success',
					timeout: 6000
				})

				this.closeMergeDialog()
				await this.fetchDuplicates()
			} catch (error) {
				console.error('Error merging:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.mergingInProgress = false
			}
		},

		closeMergeDialog() {
			this.mergeDialog = false
			this.mergeGroup = null
			this.mergeGroupKey = null
			this.mergePreview = null
		},

		findItemById(id) {
			if (!this.mergeGroup) return null
			const primary = this.getPrimary(this.mergeGroup)
			if (primary.id === id) return primary
			return this.mergeGroup.duplicates.find(d => d.id === id) || null
		},

		getPrimaryName() {
			if (!this.mergeGroup || !this.selectedPrimary[this.mergeGroupKey]) return ''
			const item = this.findItemById(this.selectedPrimary[this.mergeGroupKey])
			return item ? (item.business_name || item.code || '') : ''
		},

		getPrimaryDate() {
			if (!this.mergeGroup || !this.selectedPrimary[this.mergeGroupKey]) return ''
			const item = this.findItemById(this.selectedPrimary[this.mergeGroupKey])
			return item ? item.created_at : ''
		},

		getItemsToDelete() {
			if (!this.mergeGroup || !this.selectedPrimary[this.mergeGroupKey]) return []
			const primaryId = this.selectedPrimary[this.mergeGroupKey]
			const all = [this.getPrimary(this.mergeGroup), ...this.mergeGroup.duplicates]
			return all.filter(item => item.id !== primaryId)
		},

		getRelationLabel(relation) {
			const labels = {
				quotations: 'Preventivi',
				opportunities: 'Opportunità',
				projects: 'Progetti',
				contacts: 'Contatti',
				troubles: 'Ticket',
				destinations: 'Destinazioni',
				ddts: 'DDT',
				saleorders: 'Ordini',
				saleinvoices: 'Fatture',
				contracts: 'Contratti',
				attachments: 'Allegati'
			}
			return labels[relation] || relation
		}
	}
}
</script>

<style scoped>
.duplicate-field {
	background-color: #fff3cd;
	font-weight: 600;
	position: relative;
	color: #ff5252;
}

.duplicate-field::after {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 3px;
	background-color: #ff9800;
}

.v-expansion-panel-header {
	min-height: 64px;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
	padding: 0;
}
</style>
